import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChapterLabel, FadeUp } from "./Shared";
import { ArrowRight, Award, Compass, Sparkles, BookOpen, ExternalLink } from "lucide-react";
import { FOUNDER_BOOK, CONTACT } from "@/veedu/data";

const ChefIntro = () => {
  return (
    <section
      id="chef-jomon"
      data-chapter
      data-testid="chef-jomon-section"
      className="relative min-h-[90vh] flex items-center overflow-hidden bg-ink py-16 md:py-24"
    >
      {/* Background Texture & Ambient Glows */}
      <div className="absolute inset-0 bg-material-dark pointer-events-none opacity-40" />
      <div
        className="absolute top-1/4 -left-32 w-96 h-96 rounded-full bg-brass/10 blur-[120px] pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="absolute bottom-10 right-0 w-[420px] h-[420px] rounded-full bg-[#C4572D]/10 blur-[140px] pointer-events-none"
        aria-hidden="true"
      />

      {/* Main Container */}
      <div className="relative z-10 w-full max-w-[1340px] mx-auto px-6 sm:px-8 md:px-12 lg:px-16">
        {/* Chapter Header */}
        <div className="mb-8 md:mb-12">
          <ChapterLabel num="04" name="Chef Jomon" mal="ഷെഫ് ജോമോൻ" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left Column: Portrait & Visual Frame (5 cols on lg) */}
          <div className="lg:col-span-5 flex justify-center">
            <FadeUp delay={0.2} className="relative w-full max-w-[420px]">
              {/* Outer Golden Border & Glow */}
              <div className="relative rounded-2xl p-2.5 sm:p-3 bg-gradient-to-b from-brass/35 via-brass/15 to-brass/40 shadow-[0_20px_50px_rgba(0,0,0,0.8)] border border-brass/30">
                {/* Inner Border */}
                <div className="relative overflow-hidden rounded-xl bg-charcoal aspect-[4/5] group">
                  <img
                    src="/Images/chef-jomon-portrait.jpg"
                    alt="Chef Jomon in chef whites"
                    className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105 filter brightness-[1.02] contrast-[1.04]"
                    loading="lazy"
                  />

                  {/* Warm Lighting Vignette Overlays */}
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent opacity-85" />
                  <div className="absolute inset-0 bg-gradient-to-r from-ink/30 via-transparent to-ink/20" />

                  {/* Top-Right Badge: Executive Chef & Founder */}
                  <div className="absolute top-3.5 right-3.5 flex items-center gap-1.5 px-3 py-1 rounded-full bg-ink/80 backdrop-blur-md border border-brass/40 text-brass text-[9.5px] font-sans tracking-[0.18em] uppercase font-semibold shadow-lg">
                    <Sparkles size={11} className="text-brass shrink-0" />
                    <span>Founder & Chef</span>
                  </div>

                  {/* Bottom Portrait Caption with Authentic Signature & Journey */}
                  <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 text-left">
                    <div className="inline-flex items-center gap-3 bg-ink/75 backdrop-blur-md border border-brass/30 rounded-xl px-3.5 py-2 shadow-lg">
                      <img
                        src="/Images/Chef Jomon Text.png"
                        alt="Chef Jomon signature"
                        className="h-6 sm:h-7 w-auto object-contain filter invert opacity-95"
                      />
                      <span className="h-3.5 w-px bg-brass/40" />
                      <span className="text-cream/80 text-[10px] sm:text-[11px] font-sans tracking-[0.2em] uppercase font-medium whitespace-nowrap">
                        Kerala to London
                      </span>
                    </div>
                  </div>
                </div>

                {/* Subtle Decorative Filigree Corners */}
                <div className="absolute -top-1 -left-1 w-3 h-3 border-t-2 border-l-2 border-brass/60 rounded-tl-sm pointer-events-none" />
                <div className="absolute -top-1 -right-1 w-3 h-3 border-t-2 border-r-2 border-brass/60 rounded-tr-sm pointer-events-none" />
                <div className="absolute -bottom-1 -left-1 w-3 h-3 border-b-2 border-l-2 border-brass/60 rounded-bl-sm pointer-events-none" />
                <div className="absolute -bottom-1 -right-1 w-3 h-3 border-b-2 border-r-2 border-brass/60 rounded-br-sm pointer-events-none" />
              </div>

              {/* Quick Credentials Ribbon below portrait */}
              <div className="mt-4 flex items-center justify-between px-2 text-[10px] sm:text-[11px] font-body text-cream/60 tracking-wider uppercase">
                <span className="flex items-center gap-1 text-brass/80">
                  <Award size={12} /> Craft Guild Hero
                </span>
                <span>•</span>
                <span>BBC MasterChef Guest</span>
                <span>•</span>
                <span className="flex items-center gap-1 text-brass/80">
                  <Compass size={12} /> The LaLiT London
                </span>
              </div>
            </FadeUp>
          </div>

          {/* Right Column: Editorial & Storytelling (7 cols on lg) */}
          <div className="lg:col-span-7 space-y-6">
            <FadeUp delay={0.25}>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-brass/30 bg-brass/10 text-brass text-[10.5px] font-sans tracking-[0.2em] uppercase font-medium mb-3">
                <span>The Culinary Mind Behind #VEEDU</span>
              </div>

              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-normal text-cream tracking-tight leading-[1.08]">
                CHEF JOMON
                <span className="block font-display italic font-normal text-brass text-lg sm:text-2xl lg:text-[1.65rem] tracking-normal mt-2 sm:mt-3 leading-snug">
                  From Kerala to London — A Culinary Journey Built on Passion, Perseverance & Purpose
                </span>
              </h2>
            </FadeUp>

            {/* Classical Divider */}
            <FadeUp delay={0.35} className="flex items-center gap-4 py-1">
              <span className="h-px w-14 bg-brass/40" />
              <span className="text-xs text-brass select-none">✦</span>
              <span className="h-px w-14 bg-brass/40" />
            </FadeUp>

            {/* Concise Excerpt Copy */}
            <FadeUp delay={0.45} className="space-y-4 text-cream/90 font-body text-[14.5px] sm:text-[15.5px] lg:text-[16px] leading-[1.75] font-light">
              <p className="text-ivory font-normal text-base sm:text-lg italic font-display leading-relaxed border-l-2 border-brass/60 pl-4 py-0.5">
                Every dish has a story.
              </p>

              <p>
                For Chef Jomon, that story began in Kerala, South India, where food was never simply food. It was family, tradition, celebration, memory and identity.
              </p>

              <p>
                Today, Chef Jomon is the founder of <strong className="text-cream font-semibold">Chef Jomon’s #VEEDU</strong> — a restaurant born from a six-year dream to create a place where the flavours, memories and soul of Kerala could meet the energy and diversity of modern India.
              </p>

              <p>
                His journey spans almost two decades of hard work, professional kitchens, leadership, sacrifice and an unwavering belief that food can tell a story. From Kerala to London and eventually to #VEEDU, his journey is one of passion, perseverance and purpose.
              </p>
            </FadeUp>

            {/* Founder's Published Memoir Spotlight Card */}
            <FadeUp delay={0.5} className="p-4 sm:p-5 rounded-2xl border border-brass/40 bg-gradient-to-r from-brass/15 via-[#1b120c]/90 to-charcoal/80 backdrop-blur-md shadow-xl flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-5">
              <div className="relative shrink-0 group">
                <div className="w-20 sm:w-24 aspect-[3/4] rounded-lg overflow-hidden shadow-[0_10px_25px_rgba(0,0,0,0.8)] border border-brass/40 bg-[#160e09]">
                  <img
                    src={FOUNDER_BOOK.cover}
                    alt={FOUNDER_BOOK.fullTitle}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <span className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full bg-brass text-ink font-sans text-[8.5px] font-bold uppercase tracking-wider shadow">
                  Book
                </span>
              </div>

              <div className="flex-1 text-center sm:text-left space-y-1.5">
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                  <span className="text-[10px] font-sans tracking-[0.2em] uppercase text-brass font-bold">
                    Founder's New Book
                  </span>
                  <span className="text-cream/40">•</span>
                  <span className="font-mal text-xs text-cream/80 tracking-wide">
                    പുസ്തകം ഇപ്പോൾ EVEME യിൽ
                  </span>
                </div>

                <h3 className="font-display text-base sm:text-lg text-cream font-medium leading-snug">
                  A.T.T.I.T.U.D.E{" "}
                  <span className="text-brass font-normal text-xs sm:text-sm italic block sm:inline sm:ml-1">
                    — A Food Tale from God’s Own Country
                  </span>
                </h3>

                <p className="font-body text-xs text-cream/75 leading-relaxed line-clamp-2">
                  From a small village in Kerala to conquering London’s culinary stage. An inspiring story of faith, vision, and culinary passion.
                </p>

                <div className="pt-1.5 flex flex-wrap items-center justify-center sm:justify-start gap-3">
                  <a
                    href={FOUNDER_BOOK.preOrderUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="chefintro-book-order-btn"
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-brass text-ink text-[10.5px] font-sans tracking-[0.16em] uppercase font-bold hover:bg-ivory hover:text-ink transition-colors shadow-sm"
                  >
                    <span>Order on EVEME</span>
                    <ExternalLink size={12} />
                  </a>

                  <Link
                    to="/chef-jomon#the-book"
                    className="text-[10.5px] font-sans tracking-[0.15em] uppercase text-brass hover:text-cream transition-colors underline underline-offset-4"
                  >
                    Read Book Story & Excerpt →
                  </Link>
                </div>
              </div>
            </FadeUp>

            {/* Action Buttons */}
            <FadeUp delay={0.55} className="pt-2 sm:pt-4 flex flex-wrap items-center gap-4">
              <Link
                to="/chef-jomon"
                data-testid="chef-jomon-read-story-btn"
                className="inline-flex h-[52px] sm:h-[56px] px-7 sm:px-8 items-center justify-center gap-3 rounded-full font-sans text-[11px] sm:text-[12px] font-semibold tracking-[0.2em] uppercase shadow-[0_10px_30px_rgba(0,0,0,0.4)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_35px_rgba(194,160,74,0.25)] group"
                style={{
                  backgroundColor: "rgba(194, 160, 74, 0.95)",
                  color: "#160F0B",
                  border: "1px solid #C2A04A",
                }}
              >
                <span>Chef Jomon's Story</span>
                <ArrowRight
                  size={15}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </Link>

              <a
                href={CONTACT.wikipedia}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-[52px] sm:h-[56px] px-6 sm:px-7 items-center justify-center gap-2 rounded-full font-sans text-[11px] sm:text-[12px] font-medium tracking-[0.2em] uppercase text-cream/80 hover:text-brass border border-brass/35 bg-charcoal/40 backdrop-blur-sm transition-all duration-300 hover:border-brass hover:bg-brass/10"
              >
                <span>Wikipedia</span>
                <span className="text-[10px] opacity-60">↗</span>
              </a>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChefIntro;
